import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import clsx from 'clsx'
import { StatusBadge } from '@/components/common/StatusBadge'
import { fromAzureLocation } from '@/config/azureRegions'
import pageStyles from '@/pages/DeploymentsPage.module.css'
import type { DeploymentRequest } from '@/types'
import type { ProvisioningStatus, ServerMode, TerraformStateStatus } from '@/types/workwise'
import { formatRelative } from '@/utils/format'
import { externalLinkProps, httpsUrlForHost, safeHttpUrl } from '@/utils/safeUrl'
import { formatUsd } from '@/utils/workwise/cost'
import type { ColumnId } from './columns'
import styles from './deployments.module.css'

const NONE = <span className={styles.empty}>—</span>

export function ModePill({ mode }: { mode: ServerMode }) {
  return <span className={clsx(styles.pill, mode === 'shared' ? styles.pillShared : styles.pillDedicated)}>{mode}</span>
}

const PROVISIONING_CLASS: Record<ProvisioningStatus, string> = {
  Pending: styles.pill,
  Provisioning: styles.pillWarn,
  Deployed: styles.pillOk,
  Failed: styles.pillError,
  Destroying: styles.pillWarn,
  Destroyed: styles.pill,
}

export function ProvisioningPill({ status }: { status: ProvisioningStatus }) {
  return <span className={clsx(styles.pill, PROVISIONING_CLASS[status])}>{status}</span>
}

const TERRAFORM_CLASS: Record<TerraformStateStatus, string> = {
  none: styles.pill,
  planned: styles.pillWarn,
  applied: styles.pillOk,
  destroyed: styles.pill,
  error: styles.pillError,
}

export function TerraformPill({ status }: { status: TerraformStateStatus }) {
  return <span className={clsx(styles.pill, TERRAFORM_CLASS[status])}>{status}</span>
}

/** An external link that is rendered only for a safe http(s) URL, otherwise plain text. */
export function SafeLink({ href, children }: { href: unknown; children: ReactNode }) {
  const safe = safeHttpUrl(href)
  if (!safe) return <>{children}</>
  return (
    <a {...externalLinkProps(safe)} className={styles.link} onClick={(e) => e.stopPropagation()}>
      {children} <ExternalLink size={11} style={{ verticalAlign: 'middle' }} />
    </a>
  )
}

/**
 * The row's `hostname` (the host of the pipeline-reported application URL; WorkWise revision 8) as a
 * link to https://<host> when it is a plain host name; "—" until it is reported.
 */
export function HostLink({ host }: { host: string | null | undefined }) {
  if (!host) return NONE
  const url = httpsUrlForHost(host)
  return url ? <SafeLink href={url}>{host}</SafeLink> : <span className={styles.mono}>{host}</span>
}

interface DeploymentCellProps {
  columnId: ColumnId
  dep: DeploymentRequest
}

/** One <td> of the deployments table. WorkWise-only columns show a dash on other products. */
export function DeploymentCell({ columnId, dep }: DeploymentCellProps) {
  const ww = dep.workwise ?? null
  const dash = <td>{NONE}</td>

  switch (columnId) {
    case 'id':
      return (
        <td className={pageStyles.cellId}>
          <Link to={`/deployments/${dep.id}`} className={styles.link} onClick={(e) => e.stopPropagation()}>
            #{dep.id}
          </Link>
        </td>
      )
    case 'name':
      return (
        <td>
          <span className={pageStyles.depName}>{dep.name}</span>
          {dep.description && <span className={pageStyles.depDesc}>{dep.description}</span>}
        </td>
      )
    case 'customer':
      return (
        <td>
          <span className={pageStyles.customerName}>{dep.customer_name}</span>
          <span className={pageStyles.tenantId}>{dep.tenant_id}</span>
        </td>
      )
    case 'environment':
      return (
        <td>
          <span className={pageStyles.envBadge}>{dep.environment}</span>
        </td>
      )
    case 'products':
      return (
        <td>
          <div className={pageStyles.products}>
            <span className={pageStyles.productCount}>{dep.items?.length ?? 0}</span>
            {dep.items?.slice(0, 2).map((item) => (
              <span key={item.product} className={pageStyles.productChip}>
                {item.product_name ?? `#${item.product}`}
              </span>
            ))}
            {(dep.items?.length ?? 0) > 2 && <span className={pageStyles.moreChip}>+{(dep.items?.length ?? 0) - 2}</span>}
          </div>
        </td>
      )
    case 'status':
      return (
        <td>
          <StatusBadge status={dep.status} />
        </td>
      )
    case 'region':
      return <td className={pageStyles.cellMuted}>{ww ? fromAzureLocation(dep.azure_region) : dep.azure_region}</td>
    case 'created':
      return <td className={pageStyles.cellMuted}>{formatRelative(dep.created_at)}</td>
    case 'provisioning':
      return <td>{dep.provisioning_status ? <ProvisioningPill status={dep.provisioning_status} /> : NONE}</td>
    case 'createdBy':
      return <td className={pageStyles.cellMuted}>{dep.created_by || dep.requested_by_name || '—'}</td>
    case 'hostname':
      return <td>{dep.hostname ? <HostLink host={dep.hostname} /> : NONE}</td>
    default:
      break
  }

  if (!ww) return dash

  switch (columnId) {
    case 'wwNumber':
      return <td className={styles.mono}>{ww.customer_number}</td>
    case 'appServer':
      return (
        <td>
          <span className={styles.mono}>{ww.app_server.name}</span>
          <span className={styles.sub}>{ww.app_server.sku}</span>
        </td>
      )
    case 'dbMode':
      return (
        <td>
          <ModePill mode={ww.db_mode} />
        </td>
      )
    case 'dbServer':
      return (
        <td>
          <span className={styles.mono}>{ww.db_server.name}</span>
          <span className={styles.sub}>{ww.db_server.shared ? 'shared' : ww.db_server.sku}</span>
        </td>
      )
    case 'gatewayMode':
      return (
        <td>
          <ModePill mode={ww.gateway_mode} />
        </td>
      )
    case 'gatewayServer':
      return (
        <td>
          <span className={styles.mono}>{ww.gateway_server.name}</span>
          <span className={styles.sub}>{ww.gateway_server.shared ? 'shared' : 'dedicated'}</span>
        </td>
      )
    case 'vmCount':
      return <td>{ww.vm_count}</td>
    case 'version':
      return <td className={styles.mono}>{ww.app_version || '—'}</td>
    case 'terraform':
      return (
        <td>
          <TerraformPill status={ww.terraform_state_status} />
        </td>
      )
    case 'cost':
      return <td>{ww.est_monthly_cost !== null && ww.est_monthly_cost !== '' ? formatUsd(Number(ww.est_monthly_cost)) : NONE}</td>
    case 'owner':
      return <td className={pageStyles.cellMuted}>{ww.owner || '—'}</td>
    case 'resourceGroup':
      return <td className={styles.mono}>{ww.resource_group ?? '—'}</td>
    case 'portal':
      return <td>{ww.azure_portal_url && safeHttpUrl(ww.azure_portal_url) ? <SafeLink href={ww.azure_portal_url}>Open</SafeLink> : NONE}</td>
    default:
      return dash
  }
}
